import React, { useState } from 'react'
import { ErrorBoundary as ReactErrorBoundary } from 'react-error-boundary'
import type { FallbackProps } from 'react-error-boundary'
import { Alert, Box, Button, Chip, IconButton, Paper, Tooltip, Typography } from '@mui/material'
import { BugReport as BugIcon, ExpandLess as CollapseIcon, ContentCopy as CopyIcon, ExpandMore as ExpandIcon, Refresh as RefreshIcon } from '@mui/icons-material'

interface ErrorBoundaryProps {
  children: React.ReactNode
  fallback?: React.ComponentType<FallbackProps>
  name?: string
}

interface CustomFallbackProps extends FallbackProps {
  name?: string
}

/**
 * Default Material UI styled error fallback component
 */
function DefaultErrorFallback({ error, resetErrorBoundary, name }: CustomFallbackProps) {
  const [showDetails, setShowDetails] = useState(false)
  const [copySuccess, setCopySuccess] = useState(false)

  const handleCopyError = async () => {
    const errorText = `Error: ${error.message}\n\nStack:\n${error.stack}`
    try {
      await navigator.clipboard.writeText(errorText)
      setCopySuccess(true)
      setTimeout(() => setCopySuccess(false), 2000)
    } catch (err) {
      console.error('Failed to copy error:', err)
    }
  }

  const handleReloadPage = () => {
    window.location.reload()
  }

  const isDevelopment = process.env.NODE_ENV === 'development'

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '200px',
        p: 3,
        textAlign: 'center'
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 3,
          maxWidth: '600px',
          width: '100%',
          backgroundColor: 'background.paper',
          border: '1px solid',
          borderColor: 'error.main'
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="center" mb={2}>
          <BugIcon color="error" sx={{ fontSize: 40, mr: 1 }} />
          <Typography variant="h5" color="error" gutterBottom>
            Something went wrong
          </Typography>
        </Box>

        <Alert severity="error" sx={{ mb: 2, textAlign: 'left' }}>
          An unexpected error occurred while loading the application.
          {name && (
            <Chip
              label={name}
              size="small"
              variant="outlined"
              sx={{ ml: 1, fontSize: '0.75rem' }}
            />
          )}
        </Alert>

        <Box display="flex" gap={2} justifyContent="center" mb={2}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<RefreshIcon />}
            onClick={resetErrorBoundary}
          >
            Try Again
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<RefreshIcon />}
            onClick={handleReloadPage}
          >
            Reload Page
          </Button>
        </Box>

        {isDevelopment && (
          <Box>
            <Button
              variant="text"
              size="small"
              startIcon={showDetails ? <CollapseIcon /> : <ExpandIcon />}
              onClick={() => setShowDetails(!showDetails)}
              sx={{ mb: 1 }}
            >
              {showDetails ? 'Hide' : 'Show'} Error Details
            </Button>

            {showDetails && (
              <Box sx={{ textAlign: 'left' }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                  <Typography variant="subtitle2" color="error">
                    Error Details (Development Mode)
                  </Typography>
                  <Tooltip title={copySuccess ? 'Copied!' : 'Copy error details'}>
                    <IconButton
                      size="small"
                      onClick={handleCopyError}
                      color={copySuccess ? 'success' : 'default'}
                    >
                      <CopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    backgroundColor: 'grey.100',
                    maxHeight: '300px',
                    overflow: 'auto'
                  }}
                >
                  <Typography variant="body2" component="pre" sx={{ fontSize: '0.75rem', fontFamily: 'monospace' }}>
                    {error.message}
                    {error.stack && `\n\n${error.stack}`}
                  </Typography>
                </Paper>
              </Box>
            )}
          </Box>
        )}
      </Paper>
    </Box>
  )
}

/**
 * Error Boundary component wrapper around react-error-boundary
 * Provides user-friendly error display with Material UI styling
 */
export function ErrorBoundary({ children, fallback, name }: ErrorBoundaryProps) {
  const handleError = (error: Error, errorInfo: React.ErrorInfo) => {
    const componentName = name || 'Component'
    console.error(`💥 ErrorBoundary caught error in ${componentName}:`, error)
    console.error('Component Stack:', errorInfo.componentStack)
  }

  // If custom fallback provided, adapt it to include the name prop
  const FallbackComponent = fallback 
    ? ({ error, resetErrorBoundary }: FallbackProps) => {
        const CustomFallback = fallback
        return <CustomFallback error={error} resetErrorBoundary={resetErrorBoundary} />
      }
    : ({ error, resetErrorBoundary }: FallbackProps) => (
        <DefaultErrorFallback error={error} resetErrorBoundary={resetErrorBoundary} name={name} />
      )

  return (
    <ReactErrorBoundary
      FallbackComponent={FallbackComponent}
      onError={handleError}
    >
      {children}
    </ReactErrorBoundary>
  )
}

// Note: react-error-boundary doesn't export useErrorHandler in this version
// Users can import directly from react-error-boundary if needed

// HOC wrapper for convenience
export function withErrorBoundary<P extends object>(
  Component: React.ComponentType<P>,
  errorBoundaryProps?: Omit<ErrorBoundaryProps, 'children'>
) {
  const WrappedComponent = (props: P) => (
    <ErrorBoundary {...errorBoundaryProps}>
      <Component {...props} />
    </ErrorBoundary>
  )
  
  WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name})`
  return WrappedComponent
} 